import { computed, defineComponent, onMounted, reactive, watch } from 'vue';
import { useStore } from 'vuex';
import { AccountAction, AccountGetter } from '@/store/account/account.module';
import { Account } from '@/store/account/account.model';
import { Role } from '@/store/role/role.model';
import { Assignment } from '@/store/assignment/assignment.model';
import { AssignmentAction, AssignmentGetter } from '@/store/assignment/assignment.module';
import { FiscalYearAction, FiscalYearGetter } from '@/store/fiscalYear/fiscalYear.module';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { ProvinceAction, ProvinceGetter } from '@/store/province/province.module';
import { Province } from '@/store/province/province.model';
import VueSelect from 'vue-select';
import LoadingComponent from '@/components/loading/Loading.vue';

export default defineComponent({
  name: 'ProvinceFilterComponent',
  components: {
    VueSelect,
    LoadingComponent,
  },
  emits: [
    'change'
  ],
  setup: (_, context) => {
    const store = useStore();
    const account = computed<Account>(() => store.getters[AccountGetter.ACCOUNT]);
    const role = computed<Role | undefined>(() => store.getters[AccountGetter.SINGLE_ROLE]);
    const assignment = computed<Assignment | undefined>(() => store.getters[AssignmentGetter.BY_ID](account.value?.id));
    const isOrs = computed<boolean>(() => store.getters[AccountGetter.IS_ROLE]('เจ้าหน้าที่ ส.กกภ.'));
    const assigned = computed(() => isOrs.value || !!assignment.value);
    const isLoading = computed(() => !isOrs.value && (store.getters[AssignmentGetter.STATUS] === 'init' || store.getters[AssignmentGetter.STATUS] === 'loading'));
    const fiscalYears = computed<FiscalYear[]>(() => (store.getters[FiscalYearGetter.ALL] as FiscalYear[]).filter(m => m.status <= 0));
    const provinces = computed<Province[]>(() => {
      const all = store.getters[ProvinceGetter.ALL] as Province[];
      return all.filter(m => m.iso !== 'TH-10');
    });

    const selected = reactive({
      fiscalYearId: null as (number | null | undefined),
      provinceId: null as (number | null | undefined),
    });

    onMounted(() => {
      store.dispatch(AccountAction.GET)
        .then(() => store.dispatch(FiscalYearAction.FETCH)
          .then(() => {
            selected.fiscalYearId = fiscalYears.value[0].id;
            switch (role.value?.name) {
              default:
                store.dispatch(ProvinceAction.FETCH)
                  .then(() => selected.provinceId = provinces.value[0].id);
                break;
              case 'ผู้แทน ร.นรม./รมต.นร':
                store.dispatch(AssignmentAction.FIND, { mode: 'MINISTER', id: account.value?.id })
                  .then(() => fetchProvince('ผู้แทน ร.นรม./รมต.นร', selected.fiscalYearId as number, account.value?.id));
                break;
              case 'ผู้ช่วยผู้ตรวจราชการ':
                store.dispatch(AssignmentAction.FIND, { mode: 'REGION', id: account.value?.id })
                  .then(() => fetchProvince('ผู้ช่วยผู้ตรวจราชการ', selected.fiscalYearId as number, account.value?.id));
                break;
              case 'เจ้าหน้าที่ประจำจังหวัด':
                store.dispatch(AssignmentAction.FIND, { mode: 'PROVINCE', id: account.value?.id })
                  .then(() => fetchProvince('เจ้าหน้าที่ประจำจังหวัด', selected.fiscalYearId as number, account.value?.id));
                break;
            }
          }));
    });

    watch(() => [selected.fiscalYearId, selected.provinceId], ([fiscalYearId, provinceId]) => {
      context.emit('change', {
        fiscalYearId: fiscalYearId,
        provinceId: provinceId,
      });
    });

    const fiscalYearChange = () => {
      fetchProvince(role.value?.name as string, selected.fiscalYearId as number, account.value?.id as string);
    };

    const fetchProvince = (roleName: string, fiscalYearId: number, userId: string) => {
      switch (roleName) {
        case 'ผู้แทน ร.นรม./รมต.นร':
          store.dispatch(ProvinceAction.FETCH_MINISTER, { fiscalYearId: fiscalYearId, userId: userId })
            .then(() => selected.provinceId = provinces.value[0].id);
          break;
        case 'ผู้ช่วยผู้ตรวจราชการ':
          store.dispatch(ProvinceAction.FETCH_REGION, { fiscalYearId: fiscalYearId, userId: userId })
            .then(() => selected.provinceId = provinces.value[0].id);
          break;
        case 'เจ้าหน้าที่ประจำจังหวัด':
          store.dispatch(ProvinceAction.FETCH_PROVINCE, { fiscalYearId: fiscalYearId, userId: userId })
            .then(() => selected.provinceId = provinces.value[0].id);
          break;
      }
    };

    return {
      role,
      fiscalYears,
      provinces,
      selected,
      assigned,
      isLoading,
      fiscalYearChange,
    };
  },
});
